/* eslint-disable @typescript-eslint/no-explicit-any */
import { useApiFetch } from '@/composables/useApiFetch'
import type { ForgotPassword, ResetPasswordWithOtpPayload } from '@/types/auth'
import type { Login, SignUp, AuthToken } from '@/types/authAccs'
import { wrapGqlRequest } from '@/services/graphqlEndPointCommonSetting/wrapGqlRequest'

export const useAuthAccsService = () => {
    const runtimeConfig = useRuntimeConfig() as {
        public?: {
            magentoApiUrl?: string
        }
    }

    const magentoApiUrl = runtimeConfig.public?.magentoApiUrl

    /**
     * Login user and return auth token
     */
    const doLogin = async (
        payload: Login
    ): Promise<{
        data: Ref<AuthToken | undefined>
        error: Ref<Error | null>
    }> => {
        const data = ref<AuthToken | undefined>(undefined)
        const error = ref<Error | null>(null)

        try {
            const query = `
              mutation GenerateCustomerToken($email: String!, $password: String!) {
                generateCustomerToken(email: $email, password: $password) {
                  token
                }
              }
            `

            const variables = {
                email: payload.email,
                password: payload.password,
            }

            const response = await wrapGqlRequest<{ token?: string }>(
                query,
                variables,
                'generateCustomerToken'
            )

            // Map GraphQL token to old REST response shape
            if (response?.token) {
                data.value = response.token
            }
        } catch (err: any) {
            error.value = err
        }

        return { data, error }
    }

    const doSignup = async (
        payload: SignUp
    ): Promise<{
        data: Ref<unknown | undefined>
        error: Ref<Error | null>
    }> => {
        const data = ref<unknown>()
        const error = ref<Error | null>(null)

        try {
            const createCustomerQuery = `
            mutation CreateCustomer($input: CustomerCreateInput!) {
                createCustomerV2(input: $input) {
                    customer {
                        id
                        email
                        firstname
                        lastname
                    }
                }
            }`

            const variables = {
                input: {
                    email: payload.email,
                    firstname: payload.firstName,
                    lastname: payload.lastName,
                    password: payload.password,
                },
            }

            const res = await wrapGqlRequest(createCustomerQuery, variables, 'createCustomerV2')

            data.value = res?.customer
        } catch (err) {
            error.value = err as Error
        }

        return {
            data,
            error,
        }
    }

    const doSendOtp = (
        payload: ForgotPassword
    ): Promise<{
        data: Ref<AuthToken | undefined>
        error: Ref<Error | null>
    }> => {
        const url = `${magentoApiUrl}/rest/default/V1/sendAndSaveOtp`
        return useApiFetch<AuthToken, Error>(url, {
            method: 'POST',
            body: { request: { email: payload.email } },
            responseType: 'text',
        })
    }

    const doResetPasswordWithOtp = async (
        payload: ResetPasswordWithOtpPayload
    ): Promise<{
        data: Ref<AuthToken | undefined>
        error: Ref<Error | null>
    }> => {
        const url = `${magentoApiUrl}/rest/default/V1/setPasswordWithOtp`
        return await useApiFetch<AuthToken, Error>(url, {
            method: 'POST',
            body: { request: { ...payload } },
            responseType: 'text',
        })
    }

    return {
        doLogin,
        doSignup,
        doSendOtp,
        doResetPasswordWithOtp,
    }
}
